import React, { useState, useEffect } from 'react';
import { Camera, Check, AlertCircle, Loader2 } from 'lucide-react';

import ScanBadgeButton from '../../../components/scan-badge-button/scan-badge-button';
import { fetchUserActivities, updateUserActivities } from '../../../utils/mongoDataOperations';

const Activities = ({ user }) => {
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [activities, setActivities] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          setIsLoading(true);
          setStatusMessage(null);
          const response = await fetchUserActivities(scannedReturnedID);
          setActivities(response.activities);
        } catch (error) {
          console.error('Error fetching user activities:', error);
          setStatusMessage({
            type: 'error',
            text: 'Failed to fetch activities. Please try again.',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID]);

  const handleActivityToggle = async (activityKey, newValue) => {
    if (!scannedReturnedID) return;

    try {
      setIsSaving(true);
      const updatedActivities = {
        ...activities,
        [activityKey]: newValue,
      };

      await updateUserActivities(scannedReturnedID, updatedActivities);
      setActivities(updatedActivities);
      setStatusMessage({
        type: 'success',
        text: `Activity "${activityKey}" ${newValue ? 'completed' : 'uncompleted'}.`,
      });

      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      console.error('Error updating activity:', error);
      setStatusMessage({
        type: 'error',
        text: 'Failed to update activity. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const StatusMessage = ({ type, text }) => (
    <div
      className={`flex items-center space-x-2 p-4 rounded-lg mb-4 ${
        type === 'success'
          ? 'bg-green-50 text-green-700'
          : 'bg-red-50 text-red-700'
      }`}>
      {type === 'success' ? (
        <Check className='h-5 w-5' />
      ) : (
        <AlertCircle className='h-5 w-5' />
      )}
      <span>{text}</span>
    </div>
  );

  const ActivityItem = ({ icon: Icon, label, activityKey, checked, onChange }) => (
    <div className='flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-3'>
      <div className='flex items-center space-x-3'>
        <div className='bg-white flex h-10 items-center justify-center rounded-full w-10 shadow-sm'>
          <Icon className='h-5 text-gray-600 w-5' />
        </div>
        <span className='font-medium text-gray-900'>{label}</span>
      </div>
      <label className='relative inline-flex items-center cursor-pointer'>
        <input
          type='checkbox'
          checked={checked || false}
          onChange={(e) => onChange(activityKey, e.target.checked)}
          disabled={isSaving}
          className='sr-only peer'
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );

  return (
    <div className='max-w-4xl mx-auto p-6'>
      <div className='flex flex-col mb-6'>
        <div className='flex items-center justify-between'>
          <div className='flex gap-4 items-center'>
            <h1 className='font-bold text-3xl'>Hacker Activities</h1>
          </div>
          <div className='flex items-center space-x-3'>
            <button
              onClick={() => {
                setActivities(null);
                setScannedReturnedID(null);
                setStatusMessage(null);
              }}
              className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
              Clear
            </button>
            <ScanBadgeButton
              scannedReturnedID={scannedReturnedID}
              setScannedReturnedID={setScannedReturnedID}
            />
          </div>
        </div>
      </div>

      {statusMessage && (
        <StatusMessage type={statusMessage.type} text={statusMessage.text} />
      )}

      <div className='bg-white p-6 rounded-lg shadow'>
        {isLoading ? (
          <div className='flex flex-col items-center justify-center py-12'>
            <Loader2 className='h-8 w-8 animate-spin text-blue-600 mb-4' />
            <p className='text-gray-600'>Loading activities...</p>
          </div>
        ) : !scannedReturnedID ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <div className='bg-gray-100 mb-4 p-4 rounded-full'>
              <Camera className='h-12 text-gray-400 w-12' />
            </div>
            <h2 className='font-semibold mb-2 text-xl text-gray-700'>
              Scan a Badge
            </h2>
            <p className='text-gray-500'>
              Scan a hacker's badge to view and update their activities.
            </p>
          </div>
        ) : (
          <>
            <h2 className='font-semibold mb-4 text-xl'>Activities</h2>
            <p className='text-gray-500 text-sm mb-4'>
              Toggle activities to mark them as completed or not completed.
            </p>

            <ActivityItem
              icon={Camera}
              label='Photobooth'
              activityKey='photobooth'
              checked={activities?.photobooth}
              onChange={handleActivityToggle}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Activities;
